import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const LAST_TYPING_TIMESTAMP_KEY_ROUTER = 'lastUserTypingTimestamp_Router';
const TYPING_GRACE_PERIOD_MS_ROUTER = 3000;

/**
 * Checks if the user is currently typing in an input field, textarea, or contenteditable element.
 * Also updates a timestamp in session storage if typing is detected.
 */
function isUserTypingRouter(): boolean { // Renamed
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }
  const tagName = activeElement.tagName.toLowerCase();
  const isContentEditable = activeElement.hasAttribute('contenteditable') && activeElement.getAttribute('contenteditable') !== 'false';
  const typing = tagName === 'input' || tagName === 'textarea' || isContentEditable;
  if (typing) {
    sessionStorage.setItem(LAST_TYPING_TIMESTAMP_KEY_ROUTER, Date.now().toString());
  }
  return typing;
}

/**
 * Checks if the user was typing within the last `TYPING_GRACE_PERIOD_MS_ROUTER`.
 */
function wasUserTypingRecentlyRouter(): boolean {
  if (isUserTypingRouter()) return true;

  const lastTypingTimeStr = sessionStorage.getItem(LAST_TYPING_TIMESTAMP_KEY_ROUTER);
  if (!lastTypingTimeStr) {
    return false;
  }
  const lastTypingTime = parseInt(lastTypingTimeStr, 10);
  return (Date.now() - lastTypingTime) < TYPING_GRACE_PERIOD_MS_ROUTER;
}

export function RouterRecoveryMiddleware() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationCount = useRef(0);
  
  // Store navigation timestamps with reduced sensitivity
  useEffect(() => {
    // Only track full page navigations, not hash or search changes
    // This helps prevent false positives
    const isFullPageNavigation = 
      sessionStorage.getItem('lastPathname') !== location.pathname;
    
    if (isFullPageNavigation) {
      const timestamp = Date.now();
      sessionStorage.setItem('lastPathname', location.pathname);
      const navigationHistory = JSON.parse(
        sessionStorage.getItem('navigationHistory') || '[]'
      );
      
      // Add current navigation to history
      navigationHistory.push({
        path: location.pathname,
        timestamp
      });
      
      // Keep only the last 10 entries
      if (navigationHistory.length > 10) {
        navigationHistory.shift();
      }
      
      sessionStorage.setItem('navigationHistory', JSON.stringify(navigationHistory));
      
      // Reset navigation counter when location changes
      navigationCount.current = 0;
      
      // Check for rapid navigation to the same route, but require more occurrences
      // This helps avoid triggering refreshes during normal navigation
      const recentNavigations = navigationHistory.filter(
        (nav: any) => nav.path === location.pathname &&
                     timestamp - nav.timestamp < 20000 // Adjusted time window to 20 seconds
      );

      // Only refresh if we detect 5+ rapid navigations to the same route
      if (recentNavigations.length > 4) { // Threshold for "rapid" loop
        if (wasUserTypingRecentlyRouter()) {
          console.warn('RouterRecoveryMiddleware: Detected rapid navigation loop, but user was typing recently. Deferring recovery.');
          sessionStorage.setItem('navigationHistory', JSON.stringify([]));
          return;
        }
        console.error(`RouterRecoveryMiddleware: Detected rapid navigation loop to ${location.pathname}. Suggesting recovery.`);
        
        sessionStorage.setItem('navigationHistory', JSON.stringify([])); // Clear history to prevent re-trigger
        sessionStorage.setItem('recoverySuggested_RouterMiddleware', `Rapid navigation loop to ${location.pathname}`);
        // window.location.reload(); // Automatic reload removed
      }
    }

    // Clean up very old entries on each navigation
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    try {
      const navigationHistory = JSON.parse(sessionStorage.getItem('navigationHistory') || '[]');
      const filteredHistory = navigationHistory.filter(
        (nav: any) => nav.timestamp > oneHourAgo
      );
      sessionStorage.setItem('navigationHistory', JSON.stringify(filteredHistory));
    } catch (err) {
      // Reset history if there's an error
      sessionStorage.setItem('navigationHistory', JSON.stringify([]));
    }
  }, [location]);
  
  return null;
}
